import React from 'react';
import { CentralEdgeDevice, CentralEdgeModem } from '@simcheck/shared-types';
import { MapPin, Network, Globe, Clock, Wifi, RadioTower } from 'lucide-react';
import { QuickCommandPanel } from './QuickCommandPanel';
import { useDateFormat } from '@/hooks/useDateFormat';

interface DeviceSummaryCardProps {
  device: CentralEdgeDevice;
  modems?: CentralEdgeModem[];
  onCommandSent?: () => void;
}

export const DeviceSummaryCard: React.FC<DeviceSummaryCardProps> = ({ device, modems, onCommandSent }) => {
  const fmt = useDateFormat();
  const formatLastSeen = (date?: Date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return fmt.formatDateTime(date);
  };

  return (
    <div className="bg-card border border-border dark:border-border-subtle rounded-lg p-6 mb-6">
      <h2 className="text-lg font-semibold text-foreground mb-4">Device Information</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Location */}
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-muted-foreground/70 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">Location</p>
              {device.address ? (
                <p className="text-sm text-foreground">{device.address}</p>
              ) : device.latitude && device.longitude ? (
                <p className="text-sm text-foreground font-mono">
                  {device.latitude.toFixed(6)}, {device.longitude.toFixed(6)}
                </p>
              ) : device.location ? (
                <p className="text-sm text-foreground">{device.location}</p>
              ) : (
                <p className="text-sm text-muted-foreground/70">N/A</p>
              )}
              {device.locationUpdatedAt && (
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Updated {formatLastSeen(device.locationUpdatedAt)}
                  {device.locationSource && ` • Source: ${device.locationSource}`}
                </p>
              )}
            </div>
          </div>

          {/* IP Addresses */}
          <div className="flex items-start gap-3">
            <Network className="w-5 h-5 text-muted-foreground/70 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">Network</p>
              <div className="space-y-1">
                {device.ipAddress && (
                  <p className="text-sm text-foreground font-mono">
                    Internal: {device.ipAddress}{device.port ? `:${device.port}` : ''}
                  </p>
                )}
                {device.externalIpAddress && (
                  <p className="text-sm text-foreground font-mono">
                    External: {device.externalIpAddress}
                  </p>
                )}
                {!device.ipAddress && !device.externalIpAddress && (
                  <p className="text-sm text-muted-foreground/70">N/A</p>
                )}
                {device.networkInterfaces && Object.keys(device.networkInterfaces).length > 0 && (
                  <div className="pt-1">
                    {Object.entries(device.networkInterfaces).map(([iface, mac]) => (
                      <p key={iface} className="text-xs text-muted-foreground font-mono">
                        {iface}: {mac}
                      </p>
                    ))}
                  </div>
                )}
                {device.backhaulModemImei && (
                  <p className="text-sm flex items-center gap-1.5">
                    <RadioTower className="w-3.5 h-3.5 text-muted-foreground/70" />
                    {device.backhaulStatus?.active ? (
                      <span className={device.backhaulStatus.isDefaultRoute ? 'text-emerald-500' : 'text-muted-foreground'}>
                        Backhaul: {device.backhaulStatus.isDefaultRoute ? 'carrying traffic' : 'standby'}
                        {device.backhaulStatus.ip ? ` (${device.backhaulStatus.ip})` : ''}
                      </span>
                    ) : device.backhaulStatus ? (
                      <span className="text-status-error">Backhaul: down</span>
                    ) : (
                      <span className="text-muted-foreground/70">Backhaul: configured, no status yet</span>
                    )}
                  </p>
                )}
                {device.wifiStatus && (
                  <p className="text-sm flex items-center gap-1.5">
                    <Wifi className="w-3.5 h-3.5 text-muted-foreground/70" />
                    {device.wifiStatus.apActive ? (
                      <span className="text-amber-500">
                        Setup mode{device.wifiStatus.apSsid ? ` — ${device.wifiStatus.apSsid}` : ''}
                      </span>
                    ) : device.wifiStatus.ssid ? (
                      <span className="text-emerald-500">
                        WiFi: {device.wifiStatus.ssid}{device.wifiStatus.ip ? ` (${device.wifiStatus.ip})` : ''}
                      </span>
                    ) : device.wifiStatus.state === 'UNSUPPORTED' ? (
                      <span className="text-muted-foreground/70">WiFi: not available</span>
                    ) : (
                      <span className="text-muted-foreground">WiFi: {device.wifiStatus.state.toLowerCase().replace(/_/g, ' ')}</span>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Status Information */}
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-muted-foreground/70 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">Status</p>
              <div className="space-y-1">
                <p className="text-sm text-foreground">
                  Last Heartbeat: <span className="font-semibold">{formatLastSeen(device.lastHeartbeat)}</span>
                </p>
                <p className="text-xs text-muted-foreground/70">{formatDate(device.lastHeartbeat)}</p>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-muted-foreground/70 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">Registration</p>
              <div className="space-y-1">
                <p className="text-sm text-foreground">
                  Created: {formatDate(device.createdAt)}
                </p>
                <p className="text-sm text-foreground">
                  Updated: {formatDate(device.updatedAt)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Third Column - Quick Command Panel */}
        <div>
          <QuickCommandPanel 
            deviceId={device.deviceId || device.id}
            modems={modems || []}
            onCommandSent={onCommandSent}
          />
        </div>
      </div>

      {/* Metadata (if exists) */}
      {device.metadata && Object.keys(device.metadata).length > 0 && (
        <div className="mt-6 pt-6 border-t border-border dark:border-border-subtle">
          <p className="text-sm font-medium text-muted-foreground mb-2">Additional Information</p>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(device.metadata).map(([key, value]) => (
              <div key={key} className="text-sm">
                <span className="text-muted-foreground">{key}:</span>
                <span className="ml-2 text-foreground">{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

